import { Component } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  ReactiveFormsModule,
  FormsModule
} from '@angular/forms';
import { Router } from '@angular/router';

import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthService } from '../../../services/auth.service';

import { Login } from '../../../models/Login';
import { UtilsService } from '../../../services/utils.service';
import { LocaleService } from '../../../services/locale.service';
import { CustomerService } from '../../../services/customer.service';
import { PostLoginNavigationService } from '../../../services/post-login-navigation.service';
import { Message } from 'primeng/message';
import { ButtonDirective } from 'primeng/button';
import { Select } from 'primeng/select';
import { FooterComponent } from '../footer/footer.component';
import { InputText } from 'primeng/inputtext';
import { Toast } from 'primeng/toast';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],

  imports: [
    ReactiveFormsModule,
    Message,
    TranslatePipe,
    ButtonDirective,
    Select,
    FormsModule,
    FooterComponent,
    InputText,
    Toast
  ]
})
export class LoginComponent {
  public loginForm: UntypedFormGroup;
  public organizationUnits: Array<any> = [];
  public selectedOrganizationUnit: string | undefined = undefined;
  public selectActiveOrganizationUnit = false;
  public loginError: string | undefined = undefined;

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private localeService: LocaleService,
    private customerService: CustomerService,
    private postLoginNavigationService: PostLoginNavigationService
  ) {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  doLogin(): void {
    const login: Login = {
      username: this.loginForm.value.username,
      password: this.loginForm.value.password
    };
    this.loginError = undefined;
    this.authService.login(login).subscribe(
      async () => {
        // A new login must never keep the previous session's patient in the
        // top bar, whether that session ended cleanly or not.
        this.customerService.setSelectedCustomer(null);
        const profile = this.authService.getUserProfile();
        // The login page runs in the default language: the user's own must
        // have loaded before the menus are built in it.
        await this.localeService.setLanguage(profile!.language);

        // If user is attached to multiple organization unit, (s)he have to
        // select which one is active
        if (
          profile?.organizationUnits &&
          profile?.organizationUnits.length > 1
        ) {
          this.organizationUnits = profile.organizationUnits;
          this.selectActiveOrganizationUnit = true;
        } else {
          this.postLoginNavigationService.redirectToApplication();
        }
      },
      (error) => {
        this.loginError = this.translateService.instant(error.message);
      }
    );
  }

  selectOrganization(): void {
    this.loginError = undefined;
    // Clear before switching so the previous unit's patient never flashes
    // in the top bar while the new organization unit is loading.
    this.customerService.setSelectedCustomer(null);
    const organizationUnit = this.organizationUnits.find(
      (item) => item.id === this.selectedOrganizationUnit
    );
    this.authService.changeActiveOrganizationUnit(organizationUnit).subscribe(
      () => {
        this.postLoginNavigationService.redirectToApplication();
      },
      (error) => {
        this.loginError = this.translateService.instant(error.message);
      }
    );
  }
}
